import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import wechatQRCode from '../assets/wechat-qrcode.webp';

interface WeChatModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function WeChatModal({ isOpen, onClose }: WeChatModalProps) {
  const { t } = useTranslation('wechat');

  useEffect(() => {
    // Prevent body scroll when modal is open
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    // Close modal on ESC key press
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4'
      onClick={onClose}
    >
      <div
        className='relative bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-8 animate-fadeIn'
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className='absolute top-4 right-4 text-white hover:text-gray-300 transition-colors'
          aria-label={t('buttons.close', { ns: 'common' })}
        >
          <svg
            className='w-8 h-8'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M6 18L18 6M6 6l12 12'
            />
          </svg>
        </button>

        {/* Modal Header */}
        <h2 className='text-2xl md:text-3xl font-bold text-white text-center mb-8'>
          {t('modal.title')}
        </h2>

        {/* QR Code Container */}
        <div className='bg-white rounded-xl p-6 mb-6'>
          {/* QR Code Image */}
          <div className='relative aspect-square bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden'>
            <img
              src={wechatQRCode}
              alt={t('modal.qrCodeAlt')}
              className='w-full h-full object-contain'
            />
          </div>
        </div>

        {/* Contact Info */}
        <div className='text-center'>
          <p className='text-white text-lg mb-2'>
            {t('modal.description')}
          </p>
        </div>
      </div>
    </div>
  );
}
